// The brand's products as prize-wheel prizes (contract v2: prizes are the
// brand's own Outfino products; each round = one winner who gets ALL of the
// round's products, quantity 1 each, for free + mandatory free shipping).
//
// The catalogue comes from the Products tab's endpoint (api/brands.js
// fetchBrandProducts, includeNonClothing) — the same rows, read the same way
// as BrandDashboard.js (productImage / productPrice / productListOf), plus the
// lighter product objects the wheel payload carries in `rounds[].products`.

/** A bare array (portal) or `{ data | products: [...] }` (embedded, paged). */
export function productListOf(list) {
  return Array.isArray(list) ? list : list?.data ?? list?.products ?? [];
}

/** What the API accepts as a prize (contract v2.2): ACTIVE (dev also UNLISTED), active, in stock. */
const PRIZE_STATUSES = ['ACTIVE', 'UNLISTED'];

/** In stock: no variants, or ≥ 1 variant with stock > 0 or untracked (null) stock. */
export function inStock(raw) {
  const vs = Array.isArray(raw?.variants) ? raw.variants : [];
  if (!vs.length) return true;
  return vs.some((v) => {
    const q = v?.inventory_quantity ?? v?.inventoryQuantity;
    return q === null || q === undefined || q === '' || Number(q) > 0;
  });
}

/**
 * @typedef {object} PrizeProduct
 * @property {string} id         the Outfino product id (what the API stores)
 * @property {string} name
 * @property {string|null} imageUrl
 * @property {number|null} price  first variant's price
 * @property {string|null} currency
 * @property {string|null} status ACTIVE | DRAFT | UNLISTED … (catalogue rows only)
 */

/** @returns {PrizeProduct|null} */
export function normalizeProduct(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id ?? raw.productId ?? raw.product_id;
  if (id === undefined || id === null) return null;
  const imgs = Array.isArray(raw.images) ? raw.images : [];
  const imageUrl = raw.imageUrl ?? raw.image_url ?? imgs[0]?.src ?? (typeof imgs[0] === 'string' ? imgs[0] : null) ?? raw.image ?? null;
  // v2.2 wheel payload: price = { amount, currency } | null; the catalogue: a number + currency.
  const priceObj = raw.price && typeof raw.price === 'object' ? raw.price : null;
  const rawPrice = priceObj ? priceObj.amount : raw.price ?? raw.price_data?.price ?? raw.variants?.[0]?.price;
  const price = rawPrice === null || rawPrice === undefined || rawPrice === '' ? null : Number(rawPrice);
  return {
    id: String(id),
    name: String(raw.name ?? raw.title ?? ''),
    imageUrl: imageUrl || null,
    price: Number.isFinite(price) ? price : null,
    currency: priceObj?.currency ?? raw.currency ?? raw.price_data?.currency ?? null,
    status: raw.product_status || raw.shopify_status || null,
    available: typeof raw.available === 'boolean' ? raw.available : inStock(raw) && raw.is_active !== false,
  };
}

/**
 * The catalogue → the products a round can hold: ACTIVE / UNLISTED ones (drafts and archived left
 * out); out-of-stock or inactive ones stay listed but can not be picked (`available: false`).
 */
export function prizeCatalogue(list) {
  return productListOf(list)
    .map(normalizeProduct)
    .filter((p) => p && (!p.status || PRIZE_STATUSES.includes(String(p.status).toUpperCase())));
}

/** A price in the product's own currency ("—" when unknown). */
export function formatPrice(p, lang) {
  if (!p || p.price === null || p.price === undefined) return '—';
  const currency = String(p.currency || 'HUF').toUpperCase();
  try {
    return new Intl.NumberFormat(lang === 'hu' ? 'hu-HU' : 'en-GB', {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'HUF' ? 0 : 2,
    }).format(p.price);
  } catch {
    return `${p.price.toLocaleString()} ${currency}`;
  }
}

/** The rounds' total retail value per currency, e.g. "189 700 Ft". */
export function totalValue(products, lang) {
  const sums = new Map();
  for (const p of products) {
    if (!p || p.price === null) continue;
    const c = String(p.currency || 'HUF').toUpperCase();
    sums.set(c, (sums.get(c) || 0) + p.price);
  }
  return [...sums.entries()].map(([currency, price]) => formatPrice({ price, currency }, lang)).join(' + ');
}

/** "Flap bag, Wallet" — the round's product names, for the auto summary. */
export const roundSummary = (products) => products.map((p) => p.name).filter(Boolean).join(', ');
